<script setup lang="ts">
import MeService from '@/core/shared/me/me.service';
import SubscriptionService from '@/core/shared/subscription/subscription.service';
import { computed, ref } from 'vue';
import { useDisplay } from 'vuetify';
import JobSlotsCounter from '../JobSlotsCounter/JobSlotsCounter.vue';
import InviteTeammateMenu from '../InviteTeammate/InviteTeammateMenu.vue';
import UserMenu from '../UserInfo/UserMenu.vue';
import NavbarIcon from './NavbarIcon.vue';
import ReferralIcon from '@/assets/svg/referral-icon.svg';
import HelpIcon from '@/assets/svg/help-circle-24.svg';
import TooltipArrow from '@/assets/svg/tooltip-arrow.svg?component';
import ActivityFeedBell from '../ActivityFeedBell/ActivityFeedBell.vue';
import PrivacyToggle from '@/components/Shared/PrivacyToggle/PrivacyToggle.vue';
import { useMainNavItems } from '@/composables/useMainNavItems';
import { useHelpButton } from '@/composables/useHelpButton';
import { useScrollFades } from '@/composables/useScrollFades';

const display = useDisplay();
const meService = new MeService();
const subscriptionService = new SubscriptionService();

const isMobile = computed(() => display.smAndDown.value);
const isDesktop = computed(() => !isMobile.value);

const canInviteTeammates = computed(() => meService.isEmployerAdmin);

// Primary navigation — shared source of truth with MobileDrawer.
const { items: mainNavItems } = useMainNavItems();
const { onHelpClick } = useHelpButton();

// Only the logo and avatar are pinned; everything between scrolls, with edge fades as the hint.
const scrollEl = ref<HTMLElement | null>(null);
const scrollContentEl = ref<HTMLElement | null>(null);
const { hasContentAbove, hasContentBelow } = useScrollFades(scrollEl, scrollContentEl);
</script>

<template>
  <v-navigation-drawer v-if="isDesktop" color="shade-880" rail width="64px" permanent>
    <div class="flex h-full flex-col items-center p-2">
      <!-- FactoryFix Logo (pinned) -->
      <v-tooltip
        location="end"
        :offset="8"
        content-class="bg-white text-shade-860 font-semibold !text-xs !py-2 !px-3 !rounded-md"
      >
        <template v-slot:activator="{ props: tooltipProps }">
          <RouterLink
            v-bind="tooltipProps"
            to="/home"
            class="flex h-12 w-12 shrink-0 items-center justify-center"
          >
            <img
              src="@/assets/svg/ff-logo.svg"
              alt="FactoryFix"
              width="27"
              height="24"
              class="h-7 w-[27px]"
            />
          </RouterLink>
        </template>
        <TooltipArrow
          alt="Home"
          width="8"
          height="16"
          class="float-left -ml-[18px] mt-[1px] fill-white"
        />
        <span>Home</span>
      </v-tooltip>

      <!-- Scroll area between the pinned logo and avatar. The -mx-2/px-2 pair keeps focus rings from being clipped. -->
      <div class="relative -mx-2 min-h-0 flex-1 self-stretch">
        <div
          ref="scrollEl"
          data-test="nav-scroll"
          class="nav-scroll h-full overflow-y-auto overflow-x-hidden px-2"
        >
          <div ref="scrollContentEl" class="flex min-h-full flex-col items-center">
            <!-- Activity Feed Bell -->
            <div class="drawer-item mb-2">
              <ActivityFeedBell />
            </div>

            <!-- Primary navigation (shared with MobileDrawer via useMainNavItems) -->
            <template v-for="item in mainNavItems" :key="item.key">
              <RouterLink
                v-if="item.visible && item.to"
                :to="item.to"
                class="drawer-item mb-0.5"
                active-class="drawer-item-active"
              >
                <NavbarIcon
                  :title="item.label"
                  :src="item.icon"
                  :selected-src="item.iconSelected"
                />
              </RouterLink>
              <div
                v-else-if="item.visible && item.onClick"
                class="drawer-item mb-0.5 cursor-pointer"
                @click="item.onClick()"
              >
                <NavbarIcon
                  :title="item.label"
                  :src="item.icon"
                  :selected-src="item.iconSelected"
                />
              </div>
            </template>

            <div
              v-if="meService.userProfile && meService.employer"
              class="mt-auto flex w-12 flex-col items-center justify-center pt-2"
            >
              <div class="mb-2 border-y border-shade-840 py-4">
                <JobSlotsCounter
                  class="drawer-item-bottom"
                  :total="subscriptionService.jobSlots"
                  :used="subscriptionService.usedJobSlots"
                />
              </div>
              <div class="drawer-item mb-0.5">
                <button type="button" data-test="help-button" @click="onHelpClick">
                  <NavbarIcon title="Help" :src="HelpIcon" :selected-src="HelpIcon" />
                </button>
              </div>
              <div class="drawer-item mb-0.5">
                <a href="https://refer.factoryfix.com" target="_blank" rel="noopener noreferrer">
                  <NavbarIcon
                    title="Referral"
                    :src="ReferralIcon"
                    :selected-src="ReferralIcon"
                    :no-hover="true"
                  />
                </a>
              </div>

              <div v-if="canInviteTeammates" class="drawer-item mb-0.5">
                <InviteTeammateMenu />
              </div>

              <div class="drawer-item mb-0.5">
                <PrivacyToggle />
              </div>
            </div>
          </div>
        </div>

        <div
          data-test="nav-fade-top"
          class="nav-fade top-0 bg-gradient-to-b"
          :class="{ 'nav-fade-visible': hasContentAbove }"
        ></div>
        <div
          data-test="nav-fade-bottom"
          class="nav-fade bottom-0 bg-gradient-to-t"
          :class="{ 'nav-fade-visible': hasContentBelow }"
        ></div>
      </div>

      <!-- User avatar (pinned) -->
      <div v-if="meService.userProfile && meService.employer" class="shrink-0 pt-1.5">
        <UserMenu :user="meService.userProfile" :employer="meService.employer" />
      </div>
    </div>
  </v-navigation-drawer>
</template>

<style scoped lang="scss">
.drawer-item {
  @apply flex h-12 w-12 shrink-0 items-center justify-center;
  @apply border-0 border-shade-840 transition-all duration-200;

  :deep(img) {
    @apply opacity-65;
  }
}

.drawer-item-active :deep(.icon-item) {
  @apply rounded-md border-[1px] border-shade-840 bg-shade-860;
}
.drawer-item-active :deep(img) {
  @apply opacity-100;
  background-image: var(--bkgImageSelected);
}
.drawer-item-bottom {
  @apply w-12 justify-center;
}

.nav-scroll {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.nav-fade {
  @apply pointer-events-none absolute inset-x-0 h-6 from-shade-880 to-transparent;
  @apply opacity-0 transition-opacity duration-200;
}
.nav-fade-visible {
  @apply opacity-100;
}
</style>
